<template>
  <div class="bct" :class="{ 'bct--fill': fillHeight }">
    <div v-if="loading" class="bct__loading">{{ loadingText }}</div>
    <p v-else-if="!items.length" class="bct__empty">{{ emptyText }}</p>
    <div v-else class="bct__scroll">
      <table class="bct__table">
        <thead>
          <tr>
            <th class="bct__col-star" />
            <th>
              <button type="button" class="bct__th-btn" @click="setSort('name')">
                АКБ<span class="bct__sort" :class="sortClass('name')">{{ sortMark('name') }}</span>
              </button>
            </th>
            <th class="bct__col-num">
              <button type="button" class="bct__th-btn" @click="setSort('stock')">
                Остаток<span class="bct__sort" :class="sortClass('stock')">{{ sortMark('stock') }}</span>
              </button>
            </th>
            <th class="bct__col-num">
              <button type="button" class="bct__th-btn" @click="setSort('bonus')">
                Бонус<span class="bct__sort" :class="sortClass('bonus')">{{ sortMark('bonus') }}</span>
              </button>
            </th>
            <th class="bct__col-num">
              <button type="button" class="bct__th-btn" @click="setSort('price')">
                Цена<span class="bct__sort" :class="sortClass('price')">{{ sortMark('price') }}</span>
              </button>
            </th>
            <th class="bct__col-type">
              <button type="button" class="bct__th-btn" @click="setSort('type')">
                AGM / EFB<span class="bct__sort" :class="sortClass('type')">{{ sortMark('type') }}</span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in displayItems" :key="rowKey(item, idx)">
            <td class="bct__col-star">
              <span
                class="bct__star"
                :class="{ 'bct__star--active': hoverItem === item }"
                aria-label="Подробнее об АКБ"
                @mouseenter="showTip(item, $event)"
                @mouseleave="scheduleHide"
              >*</span>
            </td>
            <td class="bct__name">
              <a v-if="item.href" :href="item.href" target="_blank" rel="noopener">{{ item.name }}</a>
              <template v-else>{{ item.name }}</template>
              <span v-if="item.korpus" class="bct__meta">{{ item.korpus }}</span>
            </td>
            <td class="bct__col-num">
              <span
                class="bct__stock"
                :class="stockClass(item.stock_total)"
              >{{ formatStock(item) }}</span>
            </td>
            <td class="bct__col-num">{{ formatBonus(item.bonus) }}</td>
            <td class="bct__col-num bct__price">{{ formatPrice(item.price) }}</td>
            <td class="bct__col-type">
              <span v-if="!typeTags(item).length" class="bct__type-empty">—</span>
              <span
                v-for="t in typeTags(item)"
                :key="t"
                class="bct__type-tag"
                :class="'bct__type-tag--' + t.toLowerCase()"
              >{{ t }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <transition name="bct-tip-fade">
      <div
        v-if="hoverItem"
        class="bct__tip"
        :style="tipStyle"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
      >
        <div class="bct__tip-title">{{ hoverItem.name }}</div>
        <dl class="bct__tip-list">
          <div v-if="hoverItem.brend">
            <dt>Бренд</dt>
            <dd>{{ hoverItem.brend }}</dd>
          </div>
          <div v-if="hoverItem.country">
            <dt>Страна</dt>
            <dd>{{ hoverItem.country }}</dd>
          </div>
          <div v-if="warrantyLabel(hoverItem)">
            <dt>Гарантия</dt>
            <dd>{{ warrantyLabel(hoverItem) }}</dd>
          </div>
          <div v-if="hoverItem.warehouse_days">
            <dt>Дней на складе</dt>
            <dd>{{ hoverItem.warehouse_days }}</dd>
          </div>
          <div v-if="hoverItem.artikul">
            <dt>Артикул</dt>
            <dd>{{ hoverItem.artikul }}</dd>
          </div>
          <div v-if="hoverItem.capacity || hoverItem.emkost">
            <dt>Ёмкость</dt>
            <dd>{{ hoverItem.capacity || hoverItem.emkost }} А·ч</dd>
          </div>
          <div v-if="hoverItem.starttok">
            <dt>Пусковой ток</dt>
            <dd>{{ hoverItem.starttok }} А</dd>
          </div>
          <div v-if="hoverItem.polarka">
            <dt>Полярность</dt>
            <dd>{{ hoverItem.polarka }}</dd>
          </div>
          <div v-if="dims(hoverItem)">
            <dt>Габариты</dt>
            <dd>{{ dims(hoverItem) }}</dd>
          </div>
          <div v-if="hoverItem.pricecategory">
            <dt>Категория</dt>
            <dd>{{ hoverItem.pricecategory }}</dd>
          </div>
          <div v-if="storeLines(hoverItem).length">
            <dt>По складам</dt>
            <dd>
              <ul class="bct__stores">
                <li v-for="(line, si) in storeLines(hoverItem)" :key="si">{{ line }}</li>
              </ul>
            </dd>
          </div>
          <div v-if="!hasDetailExtra(hoverItem)">
            <dt>Примечание</dt>
            <dd>Нет доп. данных в каталоге</dd>
          </div>
        </dl>
      </div>
    </transition>
  </div>
</template>

<script>
import { formatWarrantyMonths } from '@/constants/batteryAiOrderSync';

const TIP_WIDTH = 300;

export default {
  name: 'BatteryCatalogTable',
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: 'Загружаем каталог…' },
    emptyText: { type: String, default: 'Нет позиций в каталоге' },
    limit: { type: Number, default: 0 },
    fillHeight: { type: Boolean, default: false },
  },
  data() {
    return {
      hoverItem: null,
      tipStyle: {},
      hideTimer: null,
      sortKey: 'stock',
      sortDir: 'desc',
    };
  },
  computed: {
    sortedItems() {
      const list = [...this.items];
      const key = this.sortKey;
      const mul = this.sortDir === 'asc' ? 1 : -1;
      list.sort((a, b) => this.compareItems(a, b, key) * mul);
      return list;
    },
    displayItems() {
      const list = this.sortedItems;
      if (this.limit > 0) return list.slice(0, this.limit);
      return list;
    },
  },
  watch: {
    items() {
      this.hoverItem = null;
    },
  },
  beforeDestroy() {
    this.cancelHide();
  },
  methods: {
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = key === 'name' ? 'asc' : 'desc';
      }
    },
    sortMark(key) {
      if (this.sortKey !== key) return '↕';
      return this.sortDir === 'asc' ? '▲' : '▼';
    },
    sortClass(key) {
      return this.sortKey === key ? 'bct__sort--on' : '';
    },
    parseNum(val) {
      if (val == null || val === '') return null;
      const n = parseFloat(String(val).replace(/[^\d.,]/g, '').replace(',', '.'));
      return Number.isNaN(n) ? null : n;
    },
    compareItems(a, b, key) {
      if (key === 'name') {
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru');
      }
      if (key === 'stock') {
        return (Number(a.stock_total) || 0) - (Number(b.stock_total) || 0);
      }
      if (key === 'bonus') {
        return (this.parseNum(a.bonus) || 0) - (this.parseNum(b.bonus) || 0);
      }
      if (key === 'price') {
        return (this.parseNum(a.price) || 0) - (this.parseNum(b.price) || 0);
      }
      if (key === 'type') {
        return this.typeTags(a).join(',').localeCompare(this.typeTags(b).join(','), 'ru');
      }
      return 0;
    },
    rowKey(item, idx) {
      return (item.href || item.name || '') + idx;
    },
    typeTags(item) {
      if (item.battery_types && item.battery_types.length) return item.battery_types;
      const tags = [];
      const blob = `${item.pricecategory || ''} ${item.name || ''}`.toUpperCase();
      if (blob.includes('AGM') || blob.includes('АГМ')) tags.push('AGM');
      if (blob.includes('EFB') || blob.includes('ЕФБ')) tags.push('EFB');
      return tags;
    },
    formatStock(item) {
      const n = item.stock_total;
      if (n == null || n === '') return '—';
      return `${n} шт`;
    },
    formatBonus(bonus) {
      if (bonus == null || bonus === '') return '—';
      return bonus;
    },
    formatPrice(price) {
      if (price == null || price === '') return '—';
      return `${price} руб`;
    },
    stockClass(stock) {
      const n = Number(stock) || 0;
      if (n <= 0) return 'bct__stock--zero';
      if (n < 10) return 'bct__stock--low';
      if (n <= 30) return 'bct__stock--mid';
      return 'bct__stock--high';
    },
    showTip(item, event) {
      this.cancelHide();
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      let left = rect.left;
      const maxLeft = window.innerWidth - TIP_WIDTH - 12;
      if (left > maxLeft) left = maxLeft;
      if (left < 8) left = 8;

      let top = rect.bottom + 6;
      const tipMaxH = 320;
      if (top + tipMaxH > window.innerHeight - 8) {
        top = Math.max(8, rect.top - tipMaxH - 6);
      }

      this.hoverItem = item;
      this.tipStyle = {
        top: `${top}px`,
        left: `${left}px`,
        width: `${TIP_WIDTH}px`,
        zIndex: 2147483010,
      };
    },
    scheduleHide() {
      this.hideTimer = setTimeout(() => {
        this.hoverItem = null;
      }, 150);
    },
    cancelHide() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    dims(item) {
      const parts = [item.length, item.width, item.height].filter(Boolean);
      return parts.length ? parts.join(' × ') : '';
    },
    storeLines(item) {
      const lines = [];
      const stores = item.skladm || [];
      stores.forEach((s) => {
        const cnt = Number(s.count) || 0;
        const res = Number(s.reserve) || 0;
        if (cnt > 0 || res > 0) {
          let t = `${s.id}: ${cnt} шт`;
          if (res > 0) t += ` (резерв ${res})`;
          lines.push(t);
        }
      });
      return lines;
    },
    warrantyLabel(item) {
      const m = formatWarrantyMonths(item);
      return m ? `${m} мес.` : '';
    },
    hasDetailExtra(item) {
      return !!(
        item.brend
        || item.country
        || this.warrantyLabel(item)
        || item.warehouse_days
        || item.artikul
        || item.starttok
        || this.dims(item)
        || this.storeLines(item).length
      );
    },
  },
};
</script>

<style scoped>
.bct--fill {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.bct__loading,
.bct__empty {
  margin: 0;
  padding: 12px 0;
  font-size: 13px;
  color: #64748b;
}

.bct__scroll {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 280px;
}

.bct--fill .bct__scroll {
  flex: 1;
  min-height: 0;
  max-height: none;
}

.bct__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.bct__table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f1f5f9;
  padding: 4px 6px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.bct__th-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 4px 2px;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;
  border-radius: 4px;
}

.bct__th-btn:hover {
  color: #005eeb;
  background: rgba(0, 94, 235, 0.08);
}

.bct__sort {
  font-size: 9px;
  opacity: 0.45;
}

.bct__sort--on {
  opacity: 1;
  color: #005eeb;
}

.bct__table td {
  padding: 8px 6px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  color: #334155;
}

.bct__col-star {
  width: 28px;
  text-align: center;
}

.bct__col-num {
  white-space: nowrap;
  text-align: right;
}

.bct__col-num .bct__th-btn {
  width: 100%;
  justify-content: flex-end;
}

.bct__col-type {
  white-space: nowrap;
}

.bct__star {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #e2e8f0;
  color: #005eeb;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  cursor: help;
  user-select: none;
}

.bct__star:hover,
.bct__star--active {
  background: #005eeb;
  color: #fff;
}

.bct__name a {
  color: #005eeb;
  font-weight: 600;
  text-decoration: none;
}

.bct__name a:hover {
  text-decoration: underline;
}

.bct__meta {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.bct__price {
  font-weight: 700;
  color: #0f172a;
}

.bct__stock--zero { color: #94a3b8; }
.bct__stock--low { color: #dc2626; font-weight: 600; }
.bct__stock--mid { color: #d97706; font-weight: 600; }
.bct__stock--high { color: #16a34a; font-weight: 600; }

.bct__type-tag {
  display: inline-block;
  margin-right: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
}

.bct__type-tag--agm {
  background: #fef3c7;
  color: #b45309;
}

.bct__type-tag--efb {
  background: #e0e7ff;
  color: #4338ca;
}

.bct__type-empty {
  color: #cbd5e1;
}

.bct__tip {
  position: fixed;
  max-height: 320px;
  overflow-y: auto;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.18);
  pointer-events: auto;
}

.bct__tip-title {
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  color: #0f172a;
}

.bct__tip-list {
  margin: 0;
  display: grid;
  gap: 5px;
}

.bct__tip-list > div {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 6px;
  font-size: 11px;
  line-height: 1.35;
}

.bct__tip-list dt {
  margin: 0;
  color: #64748b;
  font-weight: 600;
}

.bct__tip-list dd {
  margin: 0;
  color: #0f172a;
}

.bct__stores {
  margin: 0;
  padding-left: 14px;
}

.bct-tip-fade-enter-active,
.bct-tip-fade-leave-active {
  transition: opacity 0.12s ease;
}

.bct-tip-fade-enter,
.bct-tip-fade-leave-to {
  opacity: 0;
}
</style>
